// Use standard DOM types available globally (ensure tsconfig includes "dom")
// import { RequestInit, HeadersInit, BodyInit, Response } from 'node-fetch'; 
// import { Readable } from 'stream'; 

// Placeholder for SDK types

/**
 * Options for initializing the JanusAPIv2Client.
 */
export interface JanusClientOptions {
    /** Base URL of the JanusCore2 backend (e.g., "http://localhost:3000") */
    baseUrl?: string;

    /** Optional list of candidate base URLs; first responsive is selected */
    baseUrlCandidates?: Array<string | { url: string; priority?: number }>;

    /** Per-candidate probe timeout in ms (used when resolving candidates). Defaults to 1000ms. */
    endpointProbeTimeoutMs?: number;

    /** Whether to cache the resolved endpoint in-memory. Defaults to true. */
    cacheResolvedEndpoint?: boolean;

    /** Optional custom fetch implementation. Defaults to global fetch.
     *  Important: In browser environments, ensure it's bound correctly, e.g., `window.fetch.bind(window)`. */
    fetch?: typeof fetch;

    /** Optional initial authentication token (JWT). */
    token?: string;

    /** Optional request timeout in milliseconds. Defaults to 30000 (30 seconds). */
    timeout?: number;

    /** Optional configuration for the built-in client-side logger. */
    logging?: {
        /** Minimum level to log locally and potentially send to backend. Defaults to 'info'. */
        level?: 'debug' | 'info' | 'warn' | 'error';
        /** Context string to use for logs sent via the SDK's logger. Defaults to 'JanusClient'. */
        context?: string;
        /** Interval (in ms) to batch and send client logs to the backend. Defaults to 5000ms. Set to 0 to disable automatic sending. */
        sendIntervalMs?: number;
    };

    /** Optional callback function invoked when the authentication token is updated (e.g., after login).
     *  Useful for persisting the token (e.g., in localStorage). */
    onTokenUpdate?: (newToken: string | null) => void;
}

/**
 * Internal options used by the client constructor.
 */
export interface InternalOptions extends JanusClientOptions {
    // Expect global fetch type signature
    fetch: typeof globalThis.fetch; 
    timeout: number; 
}

/**
 * Options for individual API requests made via `_request`.
 */
export interface Options {
    headers?: HeadersInit;
    body?: BodyInit | object; // Allow objects for auto-JSON stringification
    params?: Record<string, string | number | boolean | undefined | null>; // Query parameters
    responseType?: 'json' | 'text' | 'blob'; // Expected response format
    skipContentTypeHeader?: boolean; // Set true for FormData to let fetch set it
}

/**
 * Base options interface for method-specific option types
 */
export interface BaseRequestOptions {
    headers?: HeadersInit;
    body?: BodyInit | object;
    params?: Record<string, string | number | boolean | undefined | null>;
    responseType?: 'json' | 'text' | 'blob';
    skipContentTypeHeader?: boolean;
}

/**
 * Export-specific options interface
 */
export interface ExportOptions extends BaseRequestOptions {
    body?: {
        format?: 'tar.zst' | 'zip';
        compression?: number;
    };
}

/**
 * Import-specific options interface  
 */
export interface ImportOptions extends BaseRequestOptions {
    // Import operations typically use application/octet-stream
    headers?: HeadersInit & {
        'Content-Type'?: 'application/octet-stream';
    };
    // Query parameters for import operations
    basePath?: string;
    pathFilter?: string;
}

/**
 * AI-specific request options for OpenAI
 */
export interface OpenAIOptions extends BaseRequestOptions {
    max_tokens?: number;
    temperature?: number;
    stream?: boolean;
    [key: string]: any;
}

/**
 * AI-specific request options for Gemini
 */
export interface GeminiOptions extends BaseRequestOptions {
    generationConfig?: {
        maxOutputTokens?: number;
        temperature?: number;
    };
    safetySettings?: any[];
    stream?: boolean;
    [key: string]: any;
}

/**
 * AI-specific request options for Anthropic
 */
export interface AnthropicOptions extends BaseRequestOptions {
    max_tokens?: number;
    temperature?: number;
    stream?: boolean;
    system?: string;
    [key: string]: any;
}

/**
 * Data upload specific options
 */
export interface DataUploadOptions extends BaseRequestOptions {
    contentType?: string;
    overwrite?: boolean;
}

/**
 * Admin operation specific options
 */
export interface AdminOptions extends BaseRequestOptions {
    timeout?: number;
}

/**
 * Log query specific options
 */
export interface LogsOptions extends BaseRequestOptions {
    // Logs typically return JSON
    responseType?: 'json';
}

// ----- Chat Types (re-exported for convenience) -----
export type {
    ChatMessage,
    ChatSinceCursor,
    JoinRoomPayload,
    JoinRoomResponse,
    ChatConnectOptions,
} from './types/chat.types';

/**
 * Represents an error returned from the Janus API.
 */
export class JanusApiError extends Error {
    readonly status: number; // HTTP status code (e.g., 400, 401, 404, 500)
    readonly errorBody?: any; // Raw error body from the response
    readonly response?: Response; // The raw fetch Response object

    constructor(message: string, status: number, errorBody?: any, response?: Response) {
        super(message);
        this.name = 'JanusApiError';
        this.status = status;
        this.errorBody = errorBody;
        this.response = response;
    }
}

// ----- Auth Related Types/DTOs -----

/**
 * Data Transfer Object for Admin Login request.
 */
export interface AdminLoginDto {
    adminKey: string;
}

/**
 * Data Transfer Object for standard User Login request.
 */
export interface LoginDto {
  email: string; // Or username, adjust based on backend implementation
  password: string;
}

/**
 * Represents a user in the system.
 */
export interface User {
    userId: string;
    email: string;
    displayName: string;
    provider: 'local' | 'google' | 'microsoft' | 'facebook';
    tenantId?: string;
    status: 'active' | 'inactive' | 'banned';
    roles: string[];
    avatarUrl?: string;
    createdAt?: string;
    updatedAt?: string;
    lastLoginAt?: string;
}

/**
 * Represents a successful authentication response.
 */
export interface AuthSuccessResponse {
    success: true;
    access_token: string;
    user: User;
}

/**
 * Represents a failed authentication response.
 */
export interface AuthFailureResponse {
    success: false;
    message: string;
    error?: string; // Optional backend error code/type
    statusCode?: number;
}

/**
 * Union type representing the possible responses from authentication endpoints.
 */
export type AuthResponseDto = AuthSuccessResponse | AuthFailureResponse;

/**
 * DTO for updating user details (used by AdminClient).
 */
export interface UpdateUserDto {
  displayName?: string;
  status?: 'active' | 'inactive' | 'banned';
  roles?: string[];
  password?: string; // Allow password update? Check backend capability
}

// ----- Data Related Types/DTOs -----

// REMOVED FileUploadData interface as it's no longer used by createOrUpdateData
// export interface FileUploadData {
//   file: Blob | Buffer | Readable; 
//   filename?: string;
//   contentType?: string;
// }

/** Base metadata common to all data items */
export interface DataItemMetadataBase {
    key: string; // Full path key (zone/path)
    uid: string; // User ID of the owner/creator
    contentType?: string;
    description?: string;
    metadata?: Record<string, any>; // User-defined metadata
    createdAt: string;
    updatedAt: string;
}

/** Metadata for blob items */
export interface BlobItemMetadata extends DataItemMetadataBase {
    originalSize: number;
    contentHash?: string; // Hash of the blob content
}

/** Combined metadata type */
export type DataItemMetadata = DataItemMetadataBase | BlobItemMetadata;

/** Represents a full data item, including content */
export type DataItem<T = any> = DataItemMetadata & {
    content: T | null; // Parsed content (e.g., JSON object) or null for non-JSON/raw blobs
};

/** DTO for querying data items */
export interface QueryDataItemsDto {
    pathPrefix?: string; // Optional: Filter by key prefix within the zone
    limit?: number; // Optional: Max items per page (default: 50)
    page?: number; // Optional: Page number (default: 1)
    sortBy?: string; // Optional: Field to sort by (e.g., 'key', 'updatedAt')
    sortOrder?: 'ASC' | 'DESC'; // Optional: Sort direction ('ASC' or 'DESC')
    metadataQuery?: Record<string, any>; // Optional: Filter by metadata fields (exact encoding TBD, consult backend)
}

/** Structure for the response of querying data items */
export interface DataQueryResponse {
  items: DataItemMetadata[];
  pagination: PaginationInfo;
}

/** DTO for creating/updating data items */
export interface CreateDataItemDto {
    content?: any; // Can be JSON, string, etc.
    description?: string;
    metadata?: Record<string, any>;
    uuid?: string; // Optional client-provided UUID
    contentHash?: string; // Optional: To bypass server-side hashing during imports
}

/** DTO for updating data item metadata (via DataClient) */
export interface UpdateDataItemDto {
    description?: string;
    metadata?: Record<string, any>;
}

// ----- Admin Related Types/DTOs -----

/** Enum for secret sources */
export enum SecretSource {
  DATABASE = 'database',
  ENVIRONMENT = 'environment', 
  OVERRIDDEN = 'overridden' // DB secret that overrides an env var
}

/** Represents a secret stub (name and metadata only, no value) */
export interface SecretStub {
    secretName: string;
    source: SecretSource;
    description?: string;
    createdAt?: string;
    updatedAt?: string;
    hasEnvironmentFallback?: boolean; // Indicates if env var exists for this name
}

/** Represents a full secret (with value and enhanced metadata) */
export interface Secret {
    secretName: string;
    value: string;
    source: SecretSource;
    description?: string;
    createdAt?: string;
    updatedAt?: string;
    hasEnvironmentFallback?: boolean; // Indicates if env var exists for this name
}

/**
 * Enum for well-known, fixed data zone identifiers.
 * Use these when interacting with fixed zones via client.data methods.
 * For dynamic zones (e.g., user-specific), pass the full string path like `'user-documents/user-123'`.
 */
export enum Zone {
    PublicContent = 'public-content',
    AdminShared = 'admin-shared',
    UserDocuments = 'user-documents', // Base path for user-specific zones
    UserUuids = 'user-uuids', // Private content with UUID access
    SecureUuids = 'secure-uuids', // Shareable content with UUID access
    AdminOnly = 'admin-only', // Application secrets and sensitive configuration
}

/** Represents data returned by the admin dashboard endpoint */
export interface DashboardData {
    stats: {
        userCount: number;
        storageCount: number;
    };
    serverInfo: {
        apiVersion?: string;
        cpuLoad?: number;
        memoryUsage?: any;
        nodeVersion?: string;
        os?: string;
        uptime?: number;
    };
    recentLogs: LogEntry[];
}

// ----- Log Related Types/DTOs -----

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Represents a log entry */
export interface LogEntry {
    timestamp: string; // Or Date?
    level: LogLevel;
    message: string;
    context?: string;
    details?: Record<string, any>; // Optional structured details
    source?: 'client' | 'server'; // Indicate origin
}

/** Represents available log levels from config */
export interface LogLevelInfo {
    level: LogLevel;
    label: string;
}

/** DTO for querying logs */
export interface LogQueryOptions {
    level?: LogLevel;
    context?: string;
    message?: string; // Search term
    startDate?: string; // ISO 8601
    endDate?: string; // ISO 8601
    limit?: number;
    offset?: number;
    order?: 'asc' | 'desc';
}

/**
 * Configuration details for a data zone.
 * Matches the structure returned by GET /api/v2/admin/zones
 */
export interface ZoneConfigurationDto {
  /** The unique identifier/key for the zone (e.g., 'public-content') */
  id: string;
  /** A user-friendly name for the zone */
  name: string;
  /** A description of the zone's purpose */
  description: string;
  /** Indicates if the zone requires authentication for access */
  requiresAuth: boolean;
  /** List of roles allowed to access this zone (if requiresAuth is true) */
  allowedRoles?: string[];
  /** Indicates if the zone is read-only */
  readOnly: boolean;
  /** Optional metadata associated with the zone configuration */
  metadata?: Record<string, any>;
}

// ----- AI Proxy Related Types/DTOs -----

/** Configuration details for an available AI provider */
export interface AiProviderDto {
    id: string;         // Unique identifier (e.g., 'openai', 'gemini', 'anthropic')
    name: string;       // User-friendly name (e.g., 'OpenAI')
    models: Array<{     // List of supported models
        id: string;     // Model ID (e.g., 'gpt-4.1-mini')
        name: string;   // Display name (e.g., 'gpt-4.1-mini')
        capabilities: string[]; // Capabilities like ['completion', 'chat']
    }>;
}

/** OpenAI Chat Message interface */
export interface OpenAIMessage {
    role: 'system' | 'user' | 'assistant';
    content: string;
}

/** Anthropic Claude Message interface */
export interface AnthropicMessage {
    role: 'user' | 'assistant';
    content: string;
}

/** Gemini Generation Configuration */
export interface GeminiGenerationConfig {
    maxOutputTokens?: number;
    temperature?: number;
    topP?: number;
    topK?: number;
}

/** Common AI request options */
export interface AiRequestOptions {
    /** HTTP headers for the request */
    headers?: Record<string, string>;
    /** Whether to use streaming response */
    stream?: boolean;
    /** Request timeout in milliseconds */
    timeout?: number;
}

export interface PaginationInfo {
  totalItems: number;
  itemCount: number; // Items on current page
  itemsPerPage: number; // Limit used
  totalPages: number;
  currentPage: number;
}

// Re-export clean interfaces from data client
export type { FileUploadOptions, UploadResult, DataQueryOptions } from './clients/data.client.js';

// === Export/Import Management ===

/**
 * Result of a successful import validation.
 */
export interface ImportValidationResult {
  success: boolean;
  message: string;
  itemCount: number;
}

/**
 * Summary of changes from an import dry-run.
 */
export interface ImportDryRunSummary {
  itemsToCreate: number;
  itemsToUpdate: number;
  itemsUnchanged: number;
  totalInArchive: number;
}

/**
 * Detailed changes from an import dry-run.
 */
export interface ImportDryRunChanges {
  create: string[];
  update: string[];
}

/**
 * Result of a successful import dry-run.
 */
export interface ImportDryRunResult {
  success: boolean;
  summary: ImportDryRunSummary;
  changes: ImportDryRunChanges;
}

/**
 * Counts of operations from an import execution.
 */
export interface ImportExecutionCounts {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
}

/**
 * Result of a successful import execution.
 */
export interface ImportExecutionResult {
  success: boolean;
  message: string;
  results: ImportExecutionCounts;
  errorDetails: string[];
}

/**
 * Export data items as a compressed tarball
 * @param paths Array of path specifiers to include in the export
 */
export interface ExportDataItemsDto {
    paths: string[];
}

/**
 * Export data items as a compressed tarball
 * @param paths Array of path specifiers to include in the export
 */
export interface ExportDataItemsResult {
    success: boolean;
    message: string;
    exportPath: string;
}

/**
 * Export data items as a compressed tarball
 * @param paths Array of path specifiers to include in the export
 */
export interface ExportDataItemsError {
    success: boolean;
    message: string;
    errorDetails: string[];
}
